import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Mail,
  Building2,
  User,
  Search,
  Trash2,
  Eye,
  MessageSquare,
  MapPin,
  Phone,
  Briefcase,
  Users,
  ChevronDown,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatCard } from "./shared";
import type { DemoRequest, PilotApplication } from "@shared/schema";

const demoStatusOptions = ["new", "reviewed", "contacted", "archived"] as const;
const pilotStatusOptions = ["new", "reviewed", "contacted", "approved", "rejected", "archived"] as const;

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "new": return "default";
    case "reviewed": return "secondary";
    case "contacted": return "outline";
    case "approved": return "default";
    case "rejected": return "destructive";
    case "archived": return "secondary";
    default: return "outline";
  }
}

export function InvestorLeadsDashboard() {
  const { toast } = useToast();
  const [demoSearch, setDemoSearch] = useState("");
  const [demoStatusFilter, setDemoStatusFilter] = useState("all");
  const [pilotSearch, setPilotSearch] = useState("");
  const [pilotStatusFilter, setPilotStatusFilter] = useState("all");
  const [selectedDemo, setSelectedDemo] = useState<DemoRequest | null>(null);
  const [selectedPilot, setSelectedPilot] = useState<PilotApplication | null>(null);

  const { data: allDemoRequests = [] } = useQuery<DemoRequest[]>({
    queryKey: ["/api/admin/demo-requests", "all-stats"],
    queryFn: async () => {
      const res = await fetch("/api/admin/demo-requests", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const { data: allPilotApplications = [] } = useQuery<PilotApplication[]>({
    queryKey: ["/api/admin/pilot-applications", "all-stats"],
    queryFn: async () => {
      const res = await fetch("/api/admin/pilot-applications", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const { data: demoRequests = [], isLoading: demosLoading } = useQuery<DemoRequest[]>({
    queryKey: ["/api/admin/demo-requests", demoSearch, demoStatusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (demoSearch) params.set("search", demoSearch);
      if (demoStatusFilter && demoStatusFilter !== "all") params.set("status", demoStatusFilter);
      const res = await fetch(`/api/admin/demo-requests?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const { data: pilotApplications = [], isLoading: pilotsLoading } = useQuery<PilotApplication[]>({
    queryKey: ["/api/admin/pilot-applications", pilotSearch, pilotStatusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (pilotSearch) params.set("search", pilotSearch);
      if (pilotStatusFilter && pilotStatusFilter !== "all") params.set("status", pilotStatusFilter);
      const res = await fetch(`/api/admin/pilot-applications?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch");
      return res.json();
    },
  });

  const updateDemoStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `/api/admin/demo-requests/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-requests"] });
      toast({ title: "Status Updated", description: "Demo request status has been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update status.", variant: "destructive" });
    },
  });

  const deleteDemo = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/demo-requests/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/demo-requests"] });
      setSelectedDemo(null);
      toast({ title: "Deleted", description: "Demo request has been removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete.", variant: "destructive" });
    },
  });

  const updatePilotStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `/api/admin/pilot-applications/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/pilot-applications"] });
      toast({ title: "Status Updated", description: "Pilot application status has been updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update status.", variant: "destructive" });
    },
  });

  const deletePilot = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/admin/pilot-applications/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/pilot-applications"] });
      setSelectedPilot(null);
      toast({ title: "Deleted", description: "Pilot application has been removed." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete.", variant: "destructive" });
    },
  });

  const newDemos = allDemoRequests.filter(d => d.status === "new").length;
  const newPilots = allPilotApplications.filter(p => p.status === "new").length;
  const totalLeads = allDemoRequests.length + allPilotApplications.length;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Demo Requests"
          value={allDemoRequests.length}
          description="Total investor demo requests"
          icon={MessageSquare}
          color="blue"
        />
        <StatCard
          title="Pilot Applications"
          value={allPilotApplications.length}
          description="Total pilot program applications"
          icon={Building2}
          color="purple"
        />
        <StatCard
          title="New Leads"
          value={newDemos + newPilots}
          description="Awaiting review"
          icon={Mail}
          color="yellow"
        />
        <StatCard
          title="Total Leads"
          value={totalLeads}
          description="All submissions combined"
          icon={Users}
          color="green"
        />
      </div>

      <Tabs defaultValue="demos" data-testid="investor-leads-tabs">
        <TabsList>
          <TabsTrigger value="demos" data-testid="tab-demo-requests">
            <MessageSquare className="w-4 h-4 mr-2" />
            Demo Requests
            {newDemos > 0 && (
              <Badge variant="destructive" className="ml-2">{newDemos}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="pilots" data-testid="tab-pilot-applications">
            <Building2 className="w-4 h-4 mr-2" />
            Pilot Applications
            {newPilots > 0 && (
              <Badge variant="destructive" className="ml-2">{newPilots}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="demos" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-4">
              <CardTitle className="text-lg">Demo Requests</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, email, company..."
                    value={demoSearch}
                    onChange={(e) => setDemoSearch(e.target.value)}
                    className="pl-9 w-60"
                    data-testid="input-demo-search"
                  />
                </div>
                <Select value={demoStatusFilter} onValueChange={setDemoStatusFilter}>
                  <SelectTrigger className="w-32" data-testid="select-demo-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {demoStatusOptions.map(s => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {demosLoading ? (
                <p className="text-muted-foreground text-sm py-8 text-center">Loading demo requests...</p>
              ) : demoRequests.length === 0 ? (
                <p className="text-muted-foreground text-sm py-8 text-center" data-testid="text-no-demo-requests">No demo requests found.</p>
              ) : (
                <div className="space-y-2">
                  {demoRequests.map((demo) => (
                    <div
                      key={demo.id}
                      className="flex items-center justify-between gap-4 p-3 rounded-md border hover-elevate cursor-pointer"
                      onClick={() => setSelectedDemo(demo)}
                      data-testid={`row-demo-${demo.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate" data-testid={`text-demo-name-${demo.id}`}>{demo.name}</span>
                            <Badge variant={statusVariant(demo.status)} data-testid={`badge-demo-status-${demo.id}`}>
                              {demo.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3" />
                              {demo.company}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {demo.email}
                            </span>
                            {demo.role && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3 h-3" />
                                {demo.role}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground hidden sm:block">
                          {demo.createdAt ? format(new Date(demo.createdAt), "MMM d, yyyy") : ""}
                        </span>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setSelectedDemo(demo); }}
                          data-testid={`button-view-demo-${demo.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pilots" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-4">
              <CardTitle className="text-lg">Pilot Applications</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by company, name, email..."
                    value={pilotSearch}
                    onChange={(e) => setPilotSearch(e.target.value)}
                    className="pl-9 w-60"
                    data-testid="input-pilot-search"
                  />
                </div>
                <Select value={pilotStatusFilter} onValueChange={setPilotStatusFilter}>
                  <SelectTrigger className="w-32" data-testid="select-pilot-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {pilotStatusOptions.map(s => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {pilotsLoading ? (
                <p className="text-muted-foreground text-sm py-8 text-center">Loading pilot applications...</p>
              ) : pilotApplications.length === 0 ? (
                <p className="text-muted-foreground text-sm py-8 text-center" data-testid="text-no-pilot-applications">No pilot applications found.</p>
              ) : (
                <div className="space-y-2">
                  {pilotApplications.map((pilot) => (
                    <div
                      key={pilot.id}
                      className="flex items-center justify-between gap-4 p-3 rounded-md border hover-elevate cursor-pointer"
                      onClick={() => setSelectedPilot(pilot)}
                      data-testid={`row-pilot-${pilot.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium truncate" data-testid={`text-pilot-company-${pilot.id}`}>{pilot.companyName}</span>
                            <Badge variant={statusVariant(pilot.status)} data-testid={`badge-pilot-status-${pilot.id}`}>
                              {pilot.status}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground mt-0.5 flex-wrap">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {pilot.applicantName}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {pilot.email}
                            </span>
                            {pilot.companyType && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3 h-3" />
                                {pilot.companyType}
                              </span>
                            )}
                            {pilot.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {pilot.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-muted-foreground hidden sm:block">
                          {pilot.createdAt ? format(new Date(pilot.createdAt), "MMM d, yyyy") : ""}
                        </span>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); setSelectedPilot(pilot); }}
                          data-testid={`button-view-pilot-${pilot.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedDemo} onOpenChange={(open) => !open && setSelectedDemo(null)}>
        <DialogContent className="max-w-lg">
          {selectedDemo && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap" data-testid="text-demo-detail-name">
                  <MessageSquare className="w-5 h-5" />
                  {selectedDemo.name}
                </DialogTitle>
                <DialogDescription>Demo request details</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Company</p>
                    <p className="text-sm font-medium" data-testid="text-demo-detail-company">{selectedDemo.company}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-sm font-medium" data-testid="text-demo-detail-email">{selectedDemo.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Role</p>
                    <p className="text-sm font-medium">{selectedDemo.role || "Not specified"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted</p>
                    <p className="text-sm font-medium">
                      {selectedDemo.createdAt ? format(new Date(selectedDemo.createdAt), "MMM d, yyyy h:mm a") : "Unknown"}
                    </p>
                  </div>
                </div>
                {selectedDemo.message && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Message</p>
                    <p className="text-sm bg-muted/50 rounded-md p-3">{selectedDemo.message}</p>
                  </div>
                )}
                <Separator />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <Select
                      value={selectedDemo.status}
                      onValueChange={(val) => {
                        updateDemoStatus.mutate({ id: selectedDemo.id, status: val });
                        setSelectedDemo({ ...selectedDemo, status: val });
                      }}
                    >
                      <SelectTrigger className="w-36" data-testid="select-demo-detail-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {demoStatusOptions.map(s => (
                          <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" data-testid="button-delete-demo">
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Demo Request</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently remove this demo request from {selectedDemo.name} at {selectedDemo.company}. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="button-cancel-delete-demo">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteDemo.mutate(selectedDemo.id)}
                          data-testid="button-confirm-delete-demo"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedPilot} onOpenChange={(open) => !open && setSelectedPilot(null)}>
        <DialogContent className="max-w-lg">
          {selectedPilot && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap" data-testid="text-pilot-detail-company">
                  <Building2 className="w-5 h-5" />
                  {selectedPilot.companyName}
                </DialogTitle>
                <DialogDescription>Pilot application details</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Applicant</p>
                    <p className="text-sm font-medium" data-testid="text-pilot-detail-name">{selectedPilot.applicantName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-sm font-medium" data-testid="text-pilot-detail-email">{selectedPilot.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Company Type</p>
                    <p className="text-sm font-medium">{selectedPilot.companyType}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Employees</p>
                    <p className="text-sm font-medium">{selectedPilot.employeeCount || "Not specified"}</p>
                  </div>
                  {selectedPilot.phone && (
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {selectedPilot.phone}
                      </p>
                    </div>
                  )}
                  {selectedPilot.location && (
                    <div>
                      <p className="text-xs text-muted-foreground">Location</p>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {selectedPilot.location}
                      </p>
                    </div>
                  )}
                  {selectedPilot.interestArea && (
                    <div>
                      <p className="text-xs text-muted-foreground">Interest Area</p>
                      <p className="text-sm font-medium">{selectedPilot.interestArea}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted</p>
                    <p className="text-sm font-medium">
                      {selectedPilot.createdAt ? format(new Date(selectedPilot.createdAt), "MMM d, yyyy h:mm a") : "Unknown"}
                    </p>
                  </div>
                </div>
                {selectedPilot.message && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Message</p>
                    <p className="text-sm bg-muted/50 rounded-md p-3">{selectedPilot.message}</p>
                  </div>
                )}
                <Separator />
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <Select
                      value={selectedPilot.status}
                      onValueChange={(val) => {
                        updatePilotStatus.mutate({ id: selectedPilot.id, status: val });
                        setSelectedPilot({ ...selectedPilot, status: val });
                      }}
                    >
                      <SelectTrigger className="w-36" data-testid="select-pilot-detail-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pilotStatusOptions.map(s => (
                          <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" data-testid="button-delete-pilot">
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Pilot Application</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently remove this pilot application from {selectedPilot.companyName}. This cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="button-cancel-delete-pilot">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deletePilot.mutate(selectedPilot.id)}
                          data-testid="button-confirm-delete-pilot"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
